import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { HelperService } from '@app/core/services/helper.service';
import { HasRequiredField } from '@app/ui-forms/classes/has-required-field';

@Component({
	selector: 'ui-select-control',
	templateUrl: './select-control.component.html',
	styleUrls: ['../../styles/form-controls.less', './select-control.component.less'],
})
export class SelectControlComponent implements OnInit {
	@Input() form: FormGroup;
	@Input() form_control: FormControl;
	@Input() label: string = null;
	@Input() info: string = null;
	@Input() options: Array<any> = null;
	@Input() empty_allowed: boolean = true;
	@Input() input_css_class: string = '';
	@Input() disabled: boolean = false;
	@Input() show_error_text: boolean = true;
	@Input() highlighted_info: boolean = false;
	@Input() placeholder: string = null;
	@Input() prefix: string = null;
	@Input() suffix: string = null;
	@Input() show_validation_icon = false;

	@Output() changed = new EventEmitter();

	form_control_id: string = '';
	form_control_name: string = '';
	is_focused: boolean = false;

	/**
	 * Constructs class and injects dependencies.
	 *
	 * @param {HelperService} helper_service
	 */
	constructor(private helper_service: HelperService) {}

	/**
	 * Lifecycle hook: Sets form control name and id.
	 */
	ngOnInit(): void {
		let form_control_name = null;
		const parent = this.form_control.parent;

		if (parent instanceof FormGroup) {
			Object.keys(parent.controls).forEach((name) => {
				if (this.form_control === parent.controls[name]) {
					form_control_name = name;
				}
			});
		}

		this.form_control_name = form_control_name;
		this.form_control_id = 'select_' + this.helper_service.generateRandomString(30);
	}

	/**
	 * Checks is control is empty or active.
	 *
	 * @returns {boolean}
	 */
	isNotEmptyOrActive(): boolean {
		return (this.form_control.value !== null && this.form_control.value !== '') || this.isInvalid();
	}

	/**
	 * Checks if control fails validation.
	 *
	 * @returns {boolean}
	 */
	isInvalid(): boolean {
		return (this.form_control.dirty || this.form_control.touched) && this.form_control.invalid;
	}

	/**
	 * Checks if control passes validation.
	 *
	 * @returns {boolean}
	 */
	isValid(): boolean {
		return this.form_control.valid && this.form_control.value;
	}

	/**
	 * Checks if control input is required.
	 *
	 * @returns {boolean}
	 */
	isRequired(): boolean {
		return HasRequiredField(this.form_control);
	}

	/**
	 * Handles when control value is changed.
	 */
	onChange(): void {
		const option_values = this.options.map((o) => {
			return o.value.toString();
		});

		const option_index = option_values.indexOf(this.form_control.value);

		this.changed.emit(this.options[option_index]);
	}

	/**
	 * Checks if info is set.
	 *
	 * @returns {boolean}
	 */
	hasInfo(): boolean {
		return typeof this.info !== 'undefined' && this.info !== null;
	}

	/**
	 * Gets error when control fails validation.
	 *
	 * @returns {string}
	 */
	getError(): string {
		if (this.isInvalid() && this.show_error_text) {
			if (this.form_control.hasError('required')) {
				return 'is required';
			}

			if (this.form_control.hasError('pattern')) {
				return 'is invalid';
			}
		}
	}

	getRequired(): string {
		if (!this.getError()) {
			return this.isRequired() ? ' *' : '';
		}
	}

	onFocus(): void {
		this.is_focused = true;
	}

	onBlur(): void {
		this.is_focused = false;
	}
}
